import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import { OptionIcon } from '@app/src/components/Form/Select'
import { ListboxComponent } from '@app/src/components/Form/SelectVirtualList'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Organization } from '@app/src/types/organizations'
import { NotificationSeverity, ONE_MINUTE_MS } from '@app/src/wf-constants'
import { Autocomplete, Grid, List, ListItem, ListItemText, TextField } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

const FlagRecalculationConfig: React.FC = () => {
  const { formatMessage } = useIntl()
  const { mutateAsync } = useCreateResource()
  const { showSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchInput])

  const { items: organizations, isLoading: isLoadingOrganizations } = useFetchCollectionWithPost<Organization>({
    endpoint: endpoints.organizationsCollection,
    key: FetchKey.Organization,
    payload: {
      filter: [
        ...(debouncedSearch
          ? [{ name: 'name', filters: [{ operator: Operators.Contains, value: debouncedSearch }] }]
          : []),
      ],
      sort: { target: 'name', order: SortOrder.ASCENDING },
      pagination: { itemsPerPage: 100, pageNumber: 1 },
      include: [],
    },
    options: {
      staleTime: ONE_MINUTE_MS,
    },
  })

  const handleRecalculateFlags = async () => {
    if (!selectedOrganization) {
      return
    }

    setLoading(true)
    try {
      await mutateAsync(
        {
          url: `${endpoints.recalculateOrganizationFlags}?organizationId=${selectedOrganization.id}`,
          body: {},
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: 'Flag recalculation triggered successfully',
              severity: NotificationSeverity.success,
            })
          },
          onError: () => {
            showSnackbar({
              message: 'Failed to trigger flag recalculation',
              severity: NotificationSeverity.error,
            })
          },
        },
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <ConfigurationCollection enablePadding={{ top: false, right: true }}>
      <List>
        <ListItem>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ListItemText
                primary="Recalculate flags for organization"
                secondary="Trigger flag recalculation for all custom questionnaires owned by the selected organization. This will re-evaluate all flag rules for the latest submitted responses. Currently, the system recalculates only questions that have active flag rules, ensuring that previously deactivated rules are not unintentionally re-enabled during a recalculation run."
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={organizations || []}
                disableListWrap
                clearOnBlur={false}
                getOptionLabel={option => `${option.id}: ${option.name}`}
                ListboxComponent={ListboxComponent}
                onChange={(_, value) => setSelectedOrganization(value)}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'reset') {
                    setSearchInput(value)
                  }
                }}
                value={selectedOrganization}
                loading={isLoadingOrganizations}
                disabled={loading}
                filterOptions={x => x}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={formatMessage({ id: 'topicalRisk.admin.organizationLabel' })}
                    placeholder={formatMessage({ id: 'topicalRisk.admin.organizationPlaceholder' })}
                    translate="no"
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <OptionIcon selected={selected} multiple={false} />
                    <span style={{ fontFamily: 'monospace' }}>{option.id}</span>: {option.name}
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} container justifyContent="flex-end" alignItems="center">
              <LoadingButton
                variant="contained"
                size="large"
                onClick={handleRecalculateFlags}
                loading={loading}
                disabled={!selectedOrganization || loading}
              >
                Recalculate flags
              </LoadingButton>
            </Grid>
          </Grid>
        </ListItem>
      </List>
    </ConfigurationCollection>
  )
}

export default FlagRecalculationConfig
